import {
  Button,
  Grid,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { dateUtils } from "../utils";
import DataTableBase from "../components/Tables/dataTableBase";
import { mapUtils } from "../utils/mapUtils";
import DeviceChangeOwnership from "../components/Dialogs/deviceChangeOwnership";
import { Router } from "@mui/icons-material";
import DeviceDeleteDialog from "../components/Dialogs/deviceDelete";
import { deviceUtils } from "../utils";
import PageTitle from "../components/Layout/PageTitle";
import CardInfo from "../components/Cards/CardInfo";
import ChangeDeviceNameButton from "../components/Buttons/ChangeDeviceName";
import { useDevice, useDeviceHistory } from "../hooks/useDevices";
import { useActionLogHistory } from "../hooks/useHistory";
import { useOrganisations } from "../hooks/useOrganisations";

export default function Device() {
  const params = useParams();
  const mapRef = React.useRef(null);

  const [showChangeOwnership, setShowChangeOwnership] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [frameColumns, setFrameColumns] = useState([
    {
      name: "fcnt",
      label: "Frame Counter",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "data",
      label: "Data",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          //input is array
          if (!value) return "N/A";
          if (!value.length) return "N/A";
          //json decode
          let data = JSON.parse(value);

          let string = "";

          //get keys and return key: value pairs
          return Object.keys(data).map((key) => {
            //if value is empty, dont return,

            //if wifimacs, return as string
            if (key === "wifi_macs") {
              //json stringify
              let wifi_macs = JSON.stringify(data[key]);
              //replace the word "mac" with bssid
              wifi_macs = wifi_macs.replace(/mac/g, "bssid");
              return (
                //add to string
                string + key + ": " + wifi_macs + ", "
              );
            }

            if (key === "isLost") {
              return (
                string + key + ": " + (data[key] ? "true" : "false") + ", "
              );
            }

            if (key === "hotspots") {
              let hotspots = data[key];

              //for each hotspot, show name and rssi
              hotspots = hotspots.map((hotspot) => {
                return (
                  string + "Name: " + hotspot.name + " RSSI: " + hotspot.rssi
                );
              });

              return (
                //add to string
                string + key + ": " + hotspots + ", "
              );
            }

            //if dont_log_frame, return nothing
            if (key === "dont_log_frame") {
              return null;
            } else {
              return (
                //add to string
                string + key + ": " + data[key] + ", "
              );
            }
          });
        },
      },
    },
    {
      name: "time_created",
      label: "Time",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return <Typography>{dateUtils.formatDate(value)}</Typography>;
        },
      },
    },
  ]);

  const {
    data: device,
    isLoading: deviceLoading,
    error: deviceError,
  } = useDevice(params.id);
  const {
    data: frameHistory,
    isLoading: historyLoading,
    error: historyError,
  } = useDeviceHistory(params.id);
  const { data: actionHistory } = useActionLogHistory({ device_id: params.id });

  const { data: organisations = [] } = useOrganisations();

  // Set up dynamic frame columns based on frame history data
  useEffect(() => {
    if (!frameHistory || !frameHistory.length) return;

    let lastFrame = frameHistory[frameHistory.length - 1];
    lastFrame = JSON.parse(lastFrame.data);

    //remove any columns where the value is null
    const columns = Object.keys(lastFrame).filter(
      (column) => lastFrame[column] !== null
    );

    const new_columns = [];

    new_columns.push(
      {
        name: "fcnt",
        label: "Frame Counter",
        options: {
          filter: true,
          sort: true,
        },
      },
      {
        name: "time_created",
        label: "Time",
        options: {
          filter: true,
          sort: true,
          customBodyRender: (value) => {
            return <Typography>{dateUtils.formatDate(value)}</Typography>;
          },
        },
      }
    );

    columns.forEach((column) => {
      if (column === "dont_log_frame") return;
      if (column === "voltage_mv") return;

      new_columns.push({
        name: "data",
        label: column,
        options: {
          filter: true,
          sort: true,
          customBodyRender: (value) => {
            //json parse
            let data = JSON.parse(value);

            //if value is true or false return "Yes" or "No"
            if (data[column] === true) return "Yes";
            if (data[column] === false) return "No";

            //if wifi macs, create a string of all the macs
            if (column === "wifi_macs") {
              //for each wifi mac, return the mac and rssi in a comma separated string
              let string = "";

              if (!data[column]) return "N/A";

              data[column].forEach((wifi_mac) => {
                string += wifi_mac.mac + " " + wifi_mac.rssi + " RSSI, ";
              });

              return string;
            }

            //data[column] may be an array or object, so json stringify if it is
            if (typeof data[column] === "object") {
              return JSON.stringify(data[column]);
            }

            //return the data
            return data[column];
          },
        },
      });
    });

    //set the columns
    setFrameColumns(new_columns);
  }, [frameHistory]);

  useEffect(() => {
    if (!device || !device.device_type) return;

    if (!frameHistory || !frameHistory.length) return;

    if (deviceUtils.isTracker(device.device_type)) {
      mapUtils.createMap(mapRef, frameHistory, device);
    }
  }, [device, frameHistory]);

  if (deviceLoading) {
    return (
      <Grid container spacing={1} justifyContent="center">
        <Grid item xs={12} textAlign="center">
          <CircularProgress />
        </Grid>
      </Grid>
    );
  }

  if (deviceError) {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Alert severity="error">
            Failed to load device: {deviceError?.message || "Unknown error"}
          </Alert>
        </Grid>
      </Grid>
    );
  }

  const actionColumns = [
    {
      name: "time_created",
      label: "Time",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return <Typography>{dateUtils.formatDate(value)}</Typography>;
        },
      },
    },
    //message, action_id
    {
      name: "message",
      label: "Message",
      options: {
        filter: true,
        sort: true,

        customBodyRender: (value) => {
          return <Typography>{value}</Typography>;
        },
      },
    },
    {
      name: "action_id",
      label: "Action",
      options: {
        filter: true,
        sort: true,
      },
    },
  ];

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <PageTitle title={device && device.name ? device.name : "N/A"}>
            <Button
              variant="outlined"
              onClick={() => {
                setShowChangeOwnership(true);
              }}
            >
              Change Ownership
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setOpenDeleteDialog(true);
              }}
            >
              Delete Device
            </Button>
            <ChangeDeviceNameButton
              id={params.id}
              name={device && device.name ? device.name : "N/A"}
            />
          </PageTitle>
        </Grid>
        <Grid item xs={12} md={3}>
          <CardInfo
            avatar={<Router sx={{ fontSize: "2em" }} />}
            title="Last Seen"
            value={
              device && device.time_updated
                ? dateUtils.formatDate(device.time_updated)
                : "N/A"
            }
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CardInfo
            avatar={<Router sx={{ fontSize: "2em" }} />}
            title="Organisation"
            value={
              device && device.organisation_id //lookup organisation name
                ? organisations && organisations.length
                  ? organisations.filter(
                      (organisation) =>
                        organisation.id === device.organisation_id
                    )[0].name
                  : "N/A"
                : "N/A"
            }
            hasEvent
            event={() => {
              window.location.href = "/organisation/" + device.organisation_id;
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CardInfo
            avatar={<Router sx={{ fontSize: "2em" }} />}
            title="Claimed?"
            value={device && device.claimed ? "True" : "False"}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CardInfo
            avatar={<Router sx={{ fontSize: "2em" }} />}
            title="DevEUI"
            value={device && device.dev_eui ? device.dev_eui : "N/A"}
          />
        </Grid>
        {device &&
        device.device_type &&
        deviceUtils.isTracker(device.device_type) ? (
          <Grid item xs={12}>
            <div id="map" />
          </Grid>
        ) : null}
        <Grid item xs={12}>
          <DataTableBase
            title="Frames"
            columns={frameColumns}
            data={frameHistory && frameHistory.length ? frameHistory : []}
            orderby="time_created"
          />
        </Grid>
      </Grid>

      <Grid item xs={12} sx={{ mt: 1 }}>
        <DataTableBase
          title="Action Alerts"
          columns={actionColumns}
          data={actionHistory && actionHistory.length ? actionHistory : []}
          orderby="time_created"
        />
      </Grid>

      <DeviceChangeOwnership
        open={showChangeOwnership}
        handleClose={() => {
          setShowChangeOwnership(false);
        }}
        device={device}
      />

      <DeviceDeleteDialog
        id={params.id}
        open={openDeleteDialog}
        handleClose={() => {
          setOpenDeleteDialog(false);
        }}
      />
    </>
  );
}
